import { type ReactNode, useState } from 'react'
import { createPortal } from 'react-dom'

import cn from '@components/utils/cn'
import Icon from '@components/utils/Icon'

/**
 * The dev-only floating overlay previews use to compare design options live.
 * Extracted from GroupInboxIndex / EventImportsPreview so every preview shares
 * one panel: a "Design tools" button pinned bottom-left, default closed, and a
 * bilingual note that none of it is part of the product.
 *
 * Portaled to document.body so `position: fixed` escapes any transformed
 * ancestor and the panel can overlap the sidebar.
 */
export function DesignExplorer({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false)

  if (typeof document === 'undefined') return null

  return createPortal(
    <>
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
        className="tw:fixed tw:bottom-4 tw:left-4 tw:z-50 tw:flex tw:items-center tw:gap-2 tw:rounded-full tw:border tw:border-gray-300 tw:bg-white tw:px-3.5 tw:py-2 tw:text-sm tw:font-medium tw:text-gray-600 tw:shadow-md tw:hover:border-primary-400 tw:hover:text-primary-600"
      >
        <Icon type="eye" className="tw:h-4 tw:w-4" aria-hidden />
        {open ? 'Hide design tools' : 'Design tools'}
      </button>

      {open && (
        <div
          role="region"
          aria-label="Design exploration"
          className="tw:fixed tw:bottom-16 tw:left-4 tw:z-50 tw:w-[min(720px,calc(100vw-2rem))] tw:rounded-xl tw:border tw:border-dashed tw:border-gray-300 tw:bg-white tw:p-5 tw:shadow-xl"
        >
          <div className="tw:mb-2 tw:flex tw:items-center tw:gap-2">
            <Icon type="eye" className="tw:h-4 tw:w-4 tw:text-gray-400" aria-hidden />
            <span className="tw:text-sm tw:font-bold tw:text-gray-700">Design exploration</span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Hide design tools"
              className="tw:ml-auto tw:flex tw:h-8 tw:w-8 tw:items-center tw:justify-center tw:rounded-lg tw:border tw:border-gray-300 tw:bg-white tw:text-gray-500 tw:hover:border-primary-400 tw:hover:text-primary-600"
            >
              <Icon type="x-close" className="tw:h-4 tw:w-4" aria-hidden />
            </button>
          </div>
          <p className="tw:mb-4 tw:text-xs tw:leading-relaxed tw:text-gray-500">
            This panel is purely for exploring the design and is not part of the product.
            <br />
            Questo pannello serve solo a esplorare il design e non fa parte del prodotto.
          </p>
          <div className="tw:flex tw:flex-wrap tw:gap-8">{children}</div>
        </div>
      )}
    </>,
    document.body
  )
}

type SegControlProps<T extends string> = {
  label: string
  value: T
  options: Array<{ value: T; label: string }>
  onChange: (value: T) => void
}

/** A labelled group of toggle buttons for one design option. */
export function SegControl<const T extends string>({ label, value, options, onChange }: SegControlProps<T>) {
  return (
    <div role="group" aria-label={label} className="tw:flex tw:flex-col tw:gap-1.5">
      <span className="tw:text-xs tw:font-bold tw:uppercase tw:tracking-wide tw:text-gray-500">{label}</span>
      <div className="tw:inline-flex tw:w-fit tw:overflow-hidden tw:rounded-lg tw:border tw:border-gray-300">
        {options.map((option, index) => (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            aria-pressed={value === option.value}
            className={cn(
              'tw:px-3 tw:py-1.5 tw:text-sm tw:font-medium',
              index > 0 && 'tw:border-l tw:border-gray-300',
              value === option.value
                ? 'tw:bg-primary-50 tw:text-primary-700'
                : 'tw:bg-white tw:text-gray-600 tw:hover:bg-gray-50'
            )}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  )
}
